import { ContactMessage, ContactMessageStatus } from '@j-s/dr-shared';

export const CLIENT_STATUS_MAP: Record<
  ContactMessageStatus,
  { label: string; color: 'danger' | 'warning' | 'success' | 'default' }
> = {
  unread: { label: 'Oczekuje na odpowiedź', color: 'danger' },
  read: { label: 'W trakcie weryfikacji', color: 'warning' },
  in_progress: { label: 'W trakcie realizacji', color: 'warning' },
  replied: { label: 'Nowa odpowiedź serwisu', color: 'success' },
  client_read: {
    label: 'Czeka na odpowiedź',
    color: 'warning',
  },
  phone_call: { label: 'Zgłoszenie telefoniczne', color: 'warning' },
  archived: { label: 'Zarchiwizowane', color: 'default' },
  closed: { label: 'Zakończona', color: 'default' },
};

export const ADMIN_STATUS_MAP: Record<
  ContactMessageStatus,
  { label: string; color: 'danger' | 'warning' | 'success' | 'default' }
> = {
  unread: { label: 'Klient oczekuje na odpowiedź', color: 'danger' },
  phone_call: { label: 'Prośba o telefon (oddzwonić)', color: 'danger' },
  replied: { label: 'Odpowiedziano (oczekiwanie na klienta)', color: 'success' },
  client_read: { label: 'Klient odczytał odpowiedź', color: 'warning' },
  read: { label: 'W trakcie obsługi', color: 'warning' },
  in_progress: { label: 'W trakcie obsługi', color: 'warning' },
  closed: { label: 'Zakończona', color: 'default' },
  archived: { label: 'Archiwum', color: 'default' },
};

export interface AdminMessageStatusInfo {
  label: string;
  badgeLabel: string;
  color: 'danger' | 'warning' | 'success' | 'default';
  description: string;
  isActionRequired: boolean;
}

export function getAdminMessageStatusInfo(
  message: ContactMessage
): AdminMessageStatusInfo {
  const lastReply = message.replies?.length
    ? message.replies[message.replies.length - 1]
    : null;

  if (message.status === 'closed') {
    const isClientClosed = message.closedBy === 'client';

    return {
      label: isClientClosed
        ? 'Klient zakończył sprawę'
        : 'Sprawa zakończona przez serwis',
      badgeLabel: isClientClosed
        ? 'Klient zakończył'
        : 'Zakończona przez serwis',
      color: 'default',
      description: isClientClosed
        ? 'Klient oznaczył tę sprawę jako zakończoną / rozwiązaną.'
        : 'Administrator serwisu oznaczył tę sprawę jako zakończoną.',
      isActionRequired: false,
    };
  }

  if (message.status === 'archived') {
    return {
      label: 'Zarchiwizowana',
      badgeLabel: 'Archiwum',
      color: 'default',
      description: 'Wiadomość została przeniesiona do archiwum.',
      isActionRequired: false,
    };
  }

  if (
    message.status === 'phone_call' ||
    (message.contactMethod === 'phone' &&
      message.phoneResponsePreference === 'call')
  ) {
    return {
      label: 'Prośba o kontakt telefoniczny (oddzwoń)',
      badgeLabel: 'Oddzwonić',
      color: 'danger',
      description:
        'Klient poprosił o kontakt telefoniczny — skontaktuj się z klientem pod wskazanym numerem telefonu.',
      isActionRequired: true,
    };
  }

  if (lastReply && lastReply.author === 'client' && message.status !== 'replied') {
    return {
      label: 'Klient odpowiedział — oczekuje na kontakt',
      badgeLabel: 'Odpowiedź klienta',
      color: 'danger',
      description: 'Klient przesłał nową wiadomość w wątku i oczekuje na odpowiedź.',
      isActionRequired: true,
    };
  }

  if (message.status === 'unread') {
    return {
      label: 'Klient oczekuje na odpowiedź',
      badgeLabel: 'Oczekuje na odpowiedź',
      color: 'danger',
      description: 'Nowe zapytanie z formularza — oczekuje na pierwszą odpowiedź serwisu.',
      isActionRequired: true,
    };
  }

  if (message.status === 'replied') {
    return {
      label: 'Odpowiedziano (oczekiwanie na klienta)',
      badgeLabel: 'Odpowiedziano',
      color: 'success',
      description: 'Odpowiedź serwisu została wysłana. Oczekiwanie na odczytanie przez klienta.',
      isActionRequired: false,
    };
  }

  if (message.status === 'client_read') {
    return {
      label: 'Klient odczytał odpowiedź',
      badgeLabel: 'Odczytana przez klienta',
      color: 'warning',
      description: message.clientReadAt
        ? `Klient zapoznał się z odpowiedzią (${new Date(message.clientReadAt).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })}). Oczekiwanie na odpowiedź lub zakończenie.`
        : 'Klient zapoznał się z odpowiedzią serwisu. Oczekiwanie na odpowiedź lub zakończenie.',
      isActionRequired: false,
    };
  }

  return {
    label: 'W trakcie obsługi',
    badgeLabel: 'W trakcie',
    color: 'warning',
    description: 'Wiadomość została odczytana przez serwis.',
    isActionRequired: false,
  };
}
